import AddEditFormCst from "@components/AddEditFormCst";
import AnimationFadeIn from "@components/AnimationFadeIn";
import BackgroundImageCst from "@components/BackgroundImageCst";
import Breadcrumb from "@components/Breadcrumb";
import HeroCst from "@components/HeroCst";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import { ColumnInterface } from "@components/Table/types";
import { useFetchDetail } from "@hooks/useFetchDetail";
import { Box, Container } from "@mui/material";
import { ServicesURL, onErrorMessage, uploadFile } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { MethodType } from "src/services/type";
import useFetchCertificatePlaceholders from "@hooks/useFetchCertificatePlaceholders";
import {
  useValidatePlaceholders,
  useValidatePlaceholdersCustom,
} from "@hooks/useValidatePlaceholders";
import { useHtmlToZip } from "@hooks/useHtmlToZip";
import { useAddBackground, useEditHTML } from "@hooks/useEditHTML";
import { axiosInstance } from "src/services/config";

const DuplicateCertificates: React.FC = () => {
  const { t } = useTranslation(["certificates-page"]);
  const { id = 1, idCertificate } = useParams();
  const navigate = useNavigate();

  const { columns, actionsForForms, dataDetail } = useFetchDetail(
    ServicesURL.getCertificates,
    idCertificate,
    PagesID["certificates"],
  );

  const formattedData = useMemo(() => {
    if (!dataDetail || Object.keys(dataDetail).length === 0) return dataDetail;
    return {
      ...dataDetail,
      name: `${dataDetail.name} - Copied`,
      id: undefined,
    };
  }, [dataDetail]);

  const formattedColumns = useMemo(() => {
    if (!columns) return columns;
    return columns.map((el: ColumnInterface) => {
      if (el.accessorKey === "name") {
        return { ...el, disabled: false };
      }
      return el;
    });
  }, [columns]);

  const { data: dataPlaceholders } = useFetchCertificatePlaceholders();

  const handleFileUnzip = useValidatePlaceholders();
  const handleFileUnzipCustom = useValidatePlaceholdersCustom();
  const handleHtmlToZip = useHtmlToZip();
  const handleEditHTML = useEditHTML();
  const handleAddBackground = useAddBackground();

  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
    description?: string;
  }>();

  const convertSvgToPng = (svgBlob: Blob): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(svgBlob);
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width || 512;
        canvas.height = img.height || 512;
        const ctx = canvas.getContext("2d");
        if (!ctx) return reject(new Error("Canvas context not available"));
        ctx.drawImage(img, 0, 0);
        canvas.toBlob((pngBlob) => {
          URL.revokeObjectURL(url);
          if (pngBlob) resolve(pngBlob);
          else reject(new Error("Canvas toBlob failed"));
        }, "image/png");
      };
      img.onerror = () => reject(new Error("SVG load failed"));
      img.src = url;
    });
  };

  const onSubmit = async (
    inputForm: any,
    fileNotificationsAsset: any,
    fileTemplateAsset: any,
    participations: any,
    fileTheme: any,
    imagePath: any,
    pdfTemplatePath: any,
    image: any,
    document: any,
    fileUploaded: any,
    isCustomizable: boolean | undefined,
    backgroundHTML: string | undefined,
    orientationHTML: string | undefined,
  ) => {
    setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
    console.log("datadetail", dataDetail);

    try {
      let templateString = inputForm.templateBody || null;

      if (templateString && templateString !== "<div><br></div>") {
        templateString = await handleEditHTML(templateString);
        templateString = await handleAddBackground(
          templateString,
          backgroundHTML,
          orientationHTML ?? "portrait",
        );
      } else {
        templateString = null;
      }

      if (!isCustomizable) {
        templateString = null;
      }

      let ObjToSend: any = {
        name: inputForm.name,
        description: inputForm.description,
        corporateId: id,
        templateBody: templateString,
      };

      let formData = new FormData();

      // Gestione template zip
      if (pdfTemplatePath?.file?.get("file")) {
        // Nuovo file caricato dall'utente
        const { placeholders } = await handleFileUnzip(
          pdfTemplatePath.file.get("file"),
          dataPlaceholders,
        );
        ObjToSend.placeholders = placeholders;
        formData.append("templateZip", pdfTemplatePath.file.get("file"));
      } else if (templateString) {
        // Template HTML custom
        const { zipFile } = await handleHtmlToZip(templateString);
        const { placeholders } = await handleFileUnzipCustom(
          zipFile,
          dataPlaceholders,
        );
        ObjToSend.placeholders = placeholders;
        formData.append("templateZip", zipFile);
      } else {
        try {
          const templateResponse = await axiosInstance.get(
            `${ServicesURL.getCertificatesTemplateZip.replace(":certificateId", String(idCertificate))}`,
            { responseType: "arraybuffer" },
          );

          const templateBlob = new Blob([templateResponse.data], {
            type: "application/zip",
          });
          console.log("zip", templateBlob);

          if (templateBlob.size < 100) {
            throw new Error(
              `Blob troppo piccolo: ${templateBlob.size} bytes, probabilmente risposta non valida`,
            );
          }

          const { placeholders } = await handleFileUnzip(
            templateBlob,
            dataPlaceholders,
          );

          ObjToSend.placeholders =
            placeholders || dataDetail.placeholders || [];
          formData.append(
            "templateZip",
            templateBlob,
            dataDetail.templateName ||
              `certificate_${idCertificate}_template.zip`,
          );
        } catch (e: any) {
          console.error("Could not fetch original template", e);
          console.error(
            "Error details:",
            e?.response?.status,
            e?.response?.data,
            e?.message,
          );
          throw new Error(
            "Impossibile recuperare il template originale. Carica manualmente il file zip.",
          );
        }
      }

      if (image?.file) {
        formData.append("image", image.file);
      } else if (dataDetail?.iconURL) {
        try {
          const response = await fetch(dataDetail.iconURL);
          const blob = await response.blob();
          console.log("blob size", blob.size, "blob type", blob.type);

          let finalBlob: Blob;
          let fileName: string;

          if (
            blob.type === "image/svg+xml" ||
            dataDetail.iconURL.endsWith(".svg")
          ) {
            finalBlob = await convertSvgToPng(blob);
            fileName = "image.png";
          } else {
            finalBlob = blob;
            fileName = `image.${dataDetail.iconURL.split(".").pop() || "png"}`;
          }

          formData.append("image", finalBlob, fileName);
        } catch (e) {
          console.error("Could not fetch original image", e);
          formData.append(
            "image",
            new Blob([], { type: "image/png" }),
            "empty.png",
          );
        }
      }

      formData.append("certificate", JSON.stringify(ObjToSend));

      await uploadFile(
        `${ServicesURL.getCertificates}`,
        formData,
        MethodType.POST,
      );
      setOpenToast({ s: StatusEnum.OK, message: "" });
    } catch (error) {
      const message = onErrorMessage(error);
      setOpenToast({ s: StatusEnum.KO, message: message });
      console.error(error);
    }
  };

  return (
    <AnimationFadeIn>
      <BackgroundImageCst
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-certificates.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={t("duplicate.hero-title")}
          description={t("duplicate.hero-description")}
        />
        {formattedColumns && (
          <Box sx={{ mt: "25px" }}>
            <AddEditFormCst
              row={formattedData}
              columns={formattedColumns}
              onSubmit={onSubmit}
              actionsForForms={
                actionsForForms &&
                actionsForForms.map((el: ColumnInterface) => {
                  if (el.accessorKey === "pdfTemplatePath") {
                    el.disabled = !dataPlaceholders;
                    el.uploadCallback = (formData: FormData) => {
                      return formData;
                    };
                  }
                  return el;
                })
              }
              backButton={() => navigate(-1)}
              fullWidth={true}
            />
          </Box>
        )}
      </Container>
      <ModalConfirmsCst
        open={!!openToast}
        title={""}
        description={""}
        onCallBackClosed={() => setOpenToast(undefined)}
        status={openToast}
      />
    </AnimationFadeIn>
  );
};

export default DuplicateCertificates;
